// =====================================================
// Swiss Finance OS - Realtime Provider
// Global provider for app-wide realtime notifications
// =====================================================

import { useEffect, useCallback, useRef, type ReactNode } from 'react';
import useRealtimeUpdates from '@/hooks/use-realtime-updates';
import { RealtimeUpdate, type TransactionProcessedData } from '@/services/realtime';
import { useToast } from '@/hooks/use-toast';
import { RealtimeContext, type RealtimeContextType } from './realtime-context';
import { useAuth } from '@/contexts/auth-context';

interface RealtimeProviderProps {
  children: ReactNode;
}

export function RealtimeProvider({ children }: RealtimeProviderProps) {
  const { toast } = useToast();
  const { user } = useAuth();

  const stableOnUpdate = useCallback((update: RealtimeUpdate) => {
    switch (update.type) {
      case 'compliance_alert': {
        const alert = update.data;
        if (alert.severity === 'urgent') {
          toast({
            title: '🚨 Urgent Compliance Alert',
            description: alert.message,
            variant: 'destructive',
            duration: 0, // Don't auto-dismiss urgent alerts
          });
        }
        break;
      }

      case 'processing_status': {
        const status = update.data;
        if (status.status === 'error') {
          toast({
            title: '❌ Processing Error',
            description: `Error processing ${status.currentFile || 'file'}`,
            variant: 'destructive',
            duration: 5000,
          });
        }
        break;
      }

      case 'transaction_processed': {
        // Only show for significant transaction batches
        const transactionData = update.data as TransactionProcessedData;
        const record = transactionData.record as { batch_size?: number };
        const batchSize = typeof record?.batch_size === 'number' ? record.batch_size : 0;

        if (transactionData.eventType === 'INSERT' && batchSize > 100) {
          toast({
            title: '✅ Large Transaction Batch Processed',
            description: `${batchSize} transactions processed successfully`,
            duration: 3000,
          });
        }
        break;
      }
    }
  }, [toast]);

  const {
    updates,
    latestUpdate,
    isConnected,
    clearUpdates,
  } = useRealtimeUpdates({
    eventTypes: 'all',
    showToasts: false, // We'll handle toasts manually for better UX
    autoConnect: !!user, // Only auto-connect when user is authenticated
    onUpdate: stableOnUpdate
  });

  const disconnectNoticeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevConnectedRef = useRef<boolean>(isConnected);


  // Show connection status changes only for authenticated users
  useEffect(() => {
    if (!user) return; // Don't show connection status for unauthenticated users

    if (!isConnected) {
      // Don't show reconnection messages if we haven't been connected yet
      if (prevConnectedRef.current === null) {
        prevConnectedRef.current = false;
        return;
      }

      // Debounce the lost-connection toast to avoid spam
      if (disconnectNoticeTimeoutRef.current) {
        clearTimeout(disconnectNoticeTimeoutRef.current);
      }
      disconnectNoticeTimeoutRef.current = setTimeout(() => {
        // Check if realtime service has detected quota error
        import('@/services/realtime').then(({ realtimeService }) => {
          if (realtimeService.isQuotaError()) {
            toast({
              title: '⚠️ API Quota Exceeded',
              description: 'Realtime updates disabled. Please contact support or upgrade your plan.',
              variant: 'destructive',
              duration: 0, // Don't auto-dismiss
            });
          } else {
            toast({
              title: '📡 Connection Lost',
              description: 'Realtime updates temporarily unavailable.',
              variant: 'default',
              duration: 5000,
            });
          }
        });
      }, 5000); // Longer debounce to avoid spam
    } else {
      // Clear any pending disconnection notices
      if (disconnectNoticeTimeoutRef.current) {
        clearTimeout(disconnectNoticeTimeoutRef.current);
        disconnectNoticeTimeoutRef.current = null;
      }

      // Show reconnection notice only if we were previously disconnected
      if (prevConnectedRef.current === false) {
        toast({
          title: '✅ Connected',
          description: 'Realtime updates restored',
          duration: 3000,
        });
      }
    }

    // Update previous status
    prevConnectedRef.current = isConnected;

    return () => {
      if (disconnectNoticeTimeoutRef.current) {
        clearTimeout(disconnectNoticeTimeoutRef.current);
        disconnectNoticeTimeoutRef.current = null;
      }
    };
  }, [isConnected, toast, user]);

  const contextValue: RealtimeContextType = {
    updates,
    latestUpdate,
    isConnected,
    clearUpdates,
  };

  return (
    <RealtimeContext.Provider value={contextValue}>
      {children}
    </RealtimeContext.Provider>
  );
}